import { faListUl } from '@fortawesome/pro-regular-svg-icons'

import { SectionWrapper, DetailInfo, DetailRow } from '@labrav/common'
import { useStyles } from './AuthorizationDetails.styles'
import {
  agreementDurationTypeViewMapping,
  discountTypeViewMapping,
} from '../../../ResellerAuthorizations/ViewDetailsResellerAuthorization/ResellerAuthorizationDetails/constants'
import { productDetailDate } from '../../../../common/utils/productCreationUtil'
import { useTimeZone } from '../../../../common/utils/hooks/useTimeZone'
import { PlanType, Truncate, LabraToolTipV2 } from '@labrav/react-components'
import type { ReceivedResellerAuthorization } from '../../../../modules/receivedResellerAuthorization/reducer'
import { memo } from 'react'
import { TruncateWithLink } from '../../../../common/components/TruncateWithLink/TruncateWithLink'
import { getCurrencySymbol } from '../../../../common/utils/getCurrencySymbol'
import { TextWithCopy } from './TextWithCopy'
import { getContractDuration } from './helpers'

export { TextWithCopy } from './TextWithCopy'

export const AuthorizationDetails = memo(
  ({
    resellerAuthorization,
  }: {
    resellerAuthorization?: ReceivedResellerAuthorization
  }) => {
    const { classes } = useStyles()
    const { timeZone } = useTimeZone()
    const { modifiedDate: endDate } =
      resellerAuthorization?.agreementExpirationDate
        ? productDetailDate(
            resellerAuthorization?.agreementExpirationDate,
            timeZone
          )
        : { modifiedDate: '' }

    const { modifiedDate: receivedOn } = resellerAuthorization?.receivedOn
      ? productDetailDate(resellerAuthorization?.receivedOn, timeZone)
      : { modifiedDate: '' }

    const discountType =
      resellerAuthorization?.discountType?.toUpperCase() || ''

    const accountId = resellerAuthorization?.isvCloudAccountId

    const sellerName = resellerAuthorization?.isvName

    const productName = resellerAuthorization?.cloudProductName

    const productType = resellerAuthorization?.productType

    const buyerAwsAccountId = resellerAuthorization?.buyerAwsAccountId

    const code = resellerAuthorization?.currency || ''

    const getLabraResaleAuthDetailPageUrl = (
      labraResellerAgreementId?: string
    ) => {
      if (labraResellerAgreementId) {
        return `${window.location.origin}/flyout/resale-authorizations/received/${labraResellerAgreementId}/details`
      }
      return ''
    }

    return (
      <SectionWrapper
        heading="Authorization details"
        icon={faListUl}
        children={
          <div className={classes.section}>
            <DetailRow
              borderBottom
              children={
                <DetailInfo
                  gridSize={12}
                  title={'Authorization name'}
                  value={
                    resellerAuthorization?.agreementName ? (
                      <TruncateWithLink
                        text={resellerAuthorization?.agreementName}
                        link={getLabraResaleAuthDetailPageUrl(
                          resellerAuthorization?.id
                        )}
                      />
                    ) : (
                      '-'
                    )
                  }
                  keyName={'agreementName'}
                />
              }
            />
            <DetailRow
              borderBottom
              children={
                <DetailInfo
                  gridSize={12}
                  title={'Authorization time length'}
                  value={
                    <div>
                      {
                        agreementDurationTypeViewMapping[
                          (
                            resellerAuthorization?.agreementDurationType || ''
                          ).toLowerCase()
                        ]
                      }
                      {endDate ? ` (${endDate})` : ''}
                    </div>
                  }
                  keyName={'agreementDurationType'}
                />
              }
            />
            <DetailRow
              borderBottom
              children={
                <DetailInfo
                  gridSize={12}
                  title={'ISV name'}
                  value={sellerName}
                  keyName={'sellerName'}
                />
              }
            />
            <DetailRow
              borderBottom
              children={
                <DetailInfo
                  gridSize={12}
                  title={'ISV AWS account ID'}
                  value={<TextWithCopy text={accountId} />}
                  keyName={'accountId'}
                />
              }
            />
            <DetailRow
              borderBottom
              children={
                <DetailInfo
                  gridSize={12}
                  title={'Product'}
                  value={
                    productName ? (
                      <Truncate width={'70%'} clipboardTooltip>
                        {productName}
                      </Truncate>
                    ) : (
                      '-'
                    )
                  }
                  keyName={'productName'}
                />
              }
            />
            <DetailRow
              borderBottom
              children={
                <DetailInfo
                  gridSize={12}
                  title={'Discount type set by ISV'}
                  value={discountTypeViewMapping[discountType]}
                  keyName={'discountType'}
                  infoTooltip={
                    'The type of discount provided to Reseller on the selected product(s) for reselling.'
                  }
                />
              }
            />
            {productType !== PlanType.SAAS_SUBSCRIPTION && (
              <DetailRow
                borderBottom
                children={
                  <DetailInfo
                    title={'Contract duration'}
                    gridSize={12}
                    value={
                      <LabraToolTipV2
                        tooltipPlacement="top-start"
                        description={
                          getContractDuration(resellerAuthorization)
                            ?.tooltipValue || ''
                        }
                      >
                        {
                          getContractDuration(resellerAuthorization)
                            ?.durationValue
                        }
                      </LabraToolTipV2>
                    }
                    keyName={'contractDurationType'}
                    infoTooltip="Which contract durations should be available for Buyer."
                  />
                }
              />
            )}

            <DetailRow
              borderBottom
              children={
                <DetailInfo
                  gridSize={12}
                  title={'Renewal'}
                  value={resellerAuthorization?.renewal ? 'Yes' : 'No'}
                  keyName={'renewal'}
                />
              }
            />
            <DetailRow
              borderBottom
              children={
                <DetailInfo
                  gridSize={12}
                  title={'Buyer AWS account ID'}
                  value={<TextWithCopy text={buyerAwsAccountId} />}
                  keyName={'buyerAwsAccountId'}
                />
              }
            />
            <DetailRow
              borderBottom
              children={
                <DetailInfo
                  gridSize={12}
                  title={'Authorization currency'}
                  value={`${code}(${getCurrencySymbol(code)})`}
                  keyName={'currency'}
                />
              }
            />

            <DetailRow
              children={
                <DetailInfo
                  gridSize={12}
                  title={'Authorization received on'}
                  value={receivedOn || '-'}
                  keyName={'receivedOn'}
                />
              }
            />
          </div>
        }
      />
    )
  }
)
